import { Application, Container } from 'pixi.js';
import { DESIGN_W, DESIGN_H, COLORS } from './theme';
import { setViewport, activeOrientation, safeInsetTop, safeInsetBottom } from './viewport';

/** Don't stretch layouts past ~22:9 — beyond this the extra height letterboxes instead. */
const MAX_VIEW_H = 1760;

/**
 * Owns the single Pixi Application and a "world" container that holds the portrait design space.
 * The world is fit to the screen **width** (always 720 wide) and top-anchored, so its **height** is
 * variable: on screens taller than the 1280 baseline (a phone in Telegram fullscreen) the world
 * fills the height with no top/bottom band, and scenes lay out against the live `viewport.h`. On
 * wide/short screens it falls back to fit-height and pillarboxes horizontally.
 */
export class PixiApp {
  readonly app: Application;
  /** Design-space root (scaled to fit). */
  readonly world: Container;
  private stableWindowWidth = 0;
  private resizeFrame = 0;

  constructor() {
    this.app = new Application();
    this.world = new Container();
  }

  async init(mount: HTMLElement): Promise<void> {
    const width = Math.max(1, window.innerWidth);
    const height = Math.max(1, window.innerHeight);
    this.stableWindowWidth = width;

    await this.app.init({
      background: COLORS.bgBottom,
      width,
      height,
      antialias: true,
      autoDensity: true,
      resolution: window.devicePixelRatio || 1,
    });
    mount.appendChild(this.app.canvas);
    this.app.stage.addChild(this.world);
    this.app.renderer.on('resize', () => this.layout());
    window.addEventListener('resize', this.queueResize);
    window.visualViewport?.addEventListener('resize', this.queueResize);
    this.layout();
  }

  /**
   * Resize Pixi for genuine viewport changes, but keep the last unobstructed canvas size while a
   * software keyboard is open. iOS/Telegram report the keyboard as a much shorter viewport; fitting
   * the 720x1280 world into that height makes the entire game suddenly zoom out.
   *
   * A meaningful width change is still honoured while editing because that indicates a real device
   * rotation rather than a keyboard opening in the current orientation.
   */
  private readonly queueResize = (): void => {
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      const width = Math.max(1, window.innerWidth);
      const height = Math.max(1, window.innerHeight);
      const active = document.activeElement;
      const editing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      const widthChanged = Math.abs(width - this.stableWindowWidth) > Math.max(2, this.stableWindowWidth * 0.1);

      if (editing && !widthChanged) return;

      this.stableWindowWidth = width;
      if (this.app.screen.width !== width || this.app.screen.height !== height) {
        this.app.renderer.resize(width, height);
      } else {
        // Telegram can change safe-area values without changing the CSS viewport dimensions.
        this.layout();
      }
    });
  };

  /**
   * Force a synchronous re-letterbox. SceneManager calls this when swapping scenes so the incoming
   * scene reads the correct (possibly orientation-swapped) viewport in `onEnter()` — Pixi's
   * `resizeTo` debounces real window resizes via rAF, which would be a frame too late.
   */
  relayout(): void {
    this.layout();
  }

  private layout(): void {
    const { width, height } = this.app.screen; // CSS px (autoDensity divides the buffer by DPR)
    // Reserve the fullscreen safe areas (notch/camera + Telegram top buttons up top, rounded corner /
    // home indicator at the bottom) so the design space maps into the usable band. 0 outside Telegram,
    // so baseline rendering is unchanged.
    const top = safeInsetTop();
    const bottom = safeInsetBottom();
    // The landscape short axis is always the portrait width (720); the long axis is the variable one.
    const LSHORT = DESIGN_W;

    if (activeOrientation() === 'landscape') {
      if (width < height) {
        // --- Case A: landscape scene on a physically PORTRAIT screen (Telegram portrait-lock, the
        // common path). Draw wide and rotate the world +90° so it fills the tall screen; the player
        // turns the phone sideways to read it upright. The notch is still physically top/bottom, so we
        // reserve the insets along the physical-vertical (long) axis. ---
        const usableLong = Math.max(1, height - top - bottom);
        const scale = Math.min(width / LSHORT, usableLong / DESIGN_H);
        const lLong = Math.min(Math.round(usableLong / scale), MAX_VIEW_H);
        this.world.rotation = Math.PI / 2;
        this.world.scale.set(scale);
        // +90° about the origin maps design (dx,dy) → screen (x − dy·scale, y + dx·scale): the long axis
        // runs DOWN the screen and the 720 strip spans [x − 720·scale, x]; center it (== width, i.e. no
        // margin, when 720·scale == width).
        this.world.x = Math.round((width + LSHORT * scale) / 2);
        this.world.y = top + Math.round((usableLong - lLong * scale) / 2);
        setViewport(lLong, LSHORT);
        return;
      }
      // --- Case B: landscape scene on a physically LANDSCAPE screen (device genuinely rotated / not
      // locked). Render upright with NO extra rotation so it isn't double-rotated. ---
      const usableShort = Math.max(1, height - top - bottom);
      const scale = Math.min(usableShort / LSHORT, width / DESIGN_H);
      const lLong = Math.min(Math.round(width / scale), MAX_VIEW_H);
      this.world.rotation = 0;
      this.world.scale.set(scale);
      this.world.x = Math.round((width - lLong * scale) / 2);
      this.world.y = top + Math.round((usableShort - LSHORT * scale) / 2);
      setViewport(lLong, LSHORT);
      return;
    }

    // --- Case C: portrait scene (every other scene) — unchanged behaviour. ---
    const usableH = Math.max(1, height - top - bottom);
    // Baseline 1280 is the *minimum* design height; the binding axis fits exactly.
    const scale = Math.min(width / DESIGN_W, usableH / DESIGN_H);
    // Live design height: fills the safe band (≥1280), capped so ultra-tall screens don't over-stretch.
    const viewH = Math.min(Math.round(usableH / scale), MAX_VIEW_H);
    this.world.rotation = 0;
    this.world.scale.set(scale);
    this.world.x = Math.round((width - DESIGN_W * scale) / 2); // 0 on phones; pillarbox on wide screens
    // Top-anchored at the top safe inset; any leftover (when viewH is capped) splits evenly below it.
    this.world.y = top + Math.round((usableH - viewH * scale) / 2);
    setViewport(DESIGN_W, viewH);
  }
}
